import type { ReactElement } from "react"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import Media from "@gatsby-plugin-generic-page/components/Media"
import Surface from "@gatsby-plugin-generic-page/components/Surface"
import type { MarketplaceMediaCard } from "@gatsby-plugin-generic-page/fragments/marketplaceMediaCardsSection"
import { Badge, CardMedia, Text } from "@ionos-web-design-system/react"
import isEmpty from "~/lib/isEmpty"
import MarketplaceMediaCardPrice from "./MarketplaceMediaCardPrice"

type MarketplaceCardProps = {
  card: MarketplaceMediaCard
  isTitleAsHeading?: boolean
}

export default function MarketplaceCard({
  card,
  isTitleAsHeading = true,
}: MarketplaceCardProps): ReactElement<MarketplaceCardProps> {
  return (
    <Surface
      className="p-2"
      roundedSize="md"
      bgColorClass="bg-surface-base"
      shadowSize="sm"
    >
      <CardMedia
        media={!isEmpty(card.image) ? <Media media={card.image} /> : undefined}
        reverse
        className="items-stretch!"
        alignment="middle"
      >
        <div className="flex flex-col justify-between h-full p-3 md:pr-0">
          <div className="flex flex-col gap-2">
            {!isEmpty(card.labels) ? (
              <div className="flex flex-wrap gap-2">
                {card.labels.map((label) => (
                  <Badge key={label} variant="neutral">
                    {label}
                  </Badge>
                ))}
              </div>
            ) : null}

            {isTitleAsHeading ? (
              <Text asChild variant="heading2xl" weight="bold">
                <h3>{card.title}</h3>
              </Text>
            ) : (
              <Text
                variant="heading2xl"
                weight="bold"
                role="heading"
                aria-level={3}
              >
                {card.title}
              </Text>
            )}

            {!isEmpty(card.description) ? (
              <MarkdownText>{card.description}</MarkdownText>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            {!isEmpty(card.price) ? (
              <div className="mt-3 mb-3">
                <MarketplaceMediaCardPrice price={card.price} />
              </div>
            ) : null}

            {!isEmpty(card.ctaElement) ? (
              <CtaElement ctaElement={card.ctaElement} />
            ) : null}
          </div>
        </div>
      </CardMedia>
    </Surface>
  )
}
